<script setup lang="ts">
import type { LastGame } from '~/types/last-game';
import type { SpacingProp } from '@/types/Layout';
import PerfilOficialSwiper from '@/components/perfil-oficial/Swiper.vue';

type SwiperControls = {
  scrollNext: () => void;
  scrollPrev: () => void;
  currentScrollLeft: Ref<number>;
  currentScrollWidth: Ref<number>;
  currentClientWidth: Ref<number>;
};

type Props = {
  title?: string;
  seeMoreUrl?: string;
  excludeSlug?: string;
  perRow?: SpacingProp;
  spaceBetween?: SpacingProp;
  cardHeight?: string;
  skipFirst?: boolean;
};

const props = withDefaults(defineProps<Props>(), {
  title: 'Vistos recentes',
  seeMoreUrl: '',
  excludeSlug: undefined,
  perRow: () => [7, 5, 4.2],
  spaceBetween: () => [16, 12, 8],
  cardHeight: 'auto',
  skipFirst: false,
});

const emit = defineEmits<{
  (e: 'seeMore'): void;
}>();

const { getLastGames, hasGames, onLastGamesUpdate } = useLastGames();
const { isAuthenticated } = storeToRefs(useAuthStore());
const { getSpacingMeasurementFromPropList } = useLayout();
const { getLoadingStrategy } = useImageLoading();

const swiperRef = ref<
  (InstanceType<typeof PerfilOficialSwiper> & SwiperControls) | null
>(null);

const lastGames = ref<LastGame[]>([]);

function loadLastGames() {
  if (isAuthenticated.value && hasGames()) {
    lastGames.value = getLastGames();
  } else {
    lastGames.value = [];
  }
}

let unsubscribe: (() => void) | null = null;

onMounted(() => {
  loadLastGames();
  unsubscribe = onLastGamesUpdate(loadLastGames);
});

onBeforeUnmount(() => {
  unsubscribe?.();
});

watch(
  isAuthenticated,
  (v) => {
    if (!v) lastGames.value = [];
    else loadLastGames();
  },
  { immediate: true },
);

// Lista filtrada/limitada do storage local
const visibleLastGames = computed<LastGame[]>(() => {
  const items = props.skipFirst
    ? lastGames.value.slice(1)
    : lastGames.value.slice();

  const filtered = props.excludeSlug
    ? items.filter((g) => g.slug !== props.excludeSlug)
    : items;

  return filtered.slice(0, 10);
});

function lastGameToGameShape(g: LastGame): Game {
  return {
    id: String(g.game_id),
    slug: g.slug,
    name: g.game_name,
    image: g.image,
    pay_today: g.pay_today != null ? String(g.pay_today) : undefined,
    provider: { id: 0, name: g.provider || '' },
    order: null,
    games: undefined,
  } as unknown as Game;
}

const mappedGames = computed<Game[]>(() =>
  visibleLastGames.value.map(lastGameToGameShape),
);

const shouldShow = computed(
  () => isAuthenticated.value && mappedGames.value.length > 0,
);

const spaceBetweenItems = computed<number>(() =>
  getSpacingMeasurementFromPropList(props.spaceBetween, 12),
);

const spaceBetweenInPixels = computed<string>(
  () => spaceBetweenItems.value + 'px',
);

const itemsPerRow = computed<number>(() =>
  getSpacingMeasurementFromPropList(props.perRow, 7),
);

const mediaItemWidth = computed<string>(() => {
  const gapValue = spaceBetweenItems.value;
  const items = itemsPerRow.value;

  return `calc((100% - ${(items - 1) * gapValue}px) / ${items})`;
});
</script>

<template>
  <div
    v-if="shouldShow"
    :class="$style.lastPlayed"
  >
    <div :class="$style.header">
      <div :class="$style.titleContainer">
        <span :class="$style.headerIcon">
          <svg
            fill="none"
            height="31"
            viewBox="0 0 32 31"
            width="32"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M6.13184 0.510742H25.5498C28.654 0.510933 31.1699 3.02764 31.1699 6.13184V24.5273C31.1699 27.6316 28.654 30.1482 25.5498 30.1484H6.13184C3.02752 30.1484 0.510742 27.6317 0.510742 24.5273V6.13184C0.510757 3.02753 3.02753 0.51076 6.13184 0.510742Z"
              stroke="white"
              stroke-opacity="0.1"
              stroke-width="1.02198"
            />
            <path
              d="M14.2866 15.5116C14.2866 14.5001 15.1066 13.6802 16.118 13.6802C17.1294 13.6802 17.9494 14.5001 17.9494 15.5116C17.9494 16.523 17.1294 17.343 16.118 17.343C15.1066 17.343 14.2866 16.523 14.2866 15.5116Z"
              fill="#FFF3F0"
            />
            <path
              clip-rule="evenodd"
              d="M7.97852 15.5116C7.97852 16.846 8.32441 17.2953 9.01621 18.1941C10.3975 19.9887 12.7142 22.0232 16.118 22.0232C19.5218 22.0232 21.8385 19.9887 23.2198 18.1941C23.9116 17.2953 24.2575 16.846 24.2575 15.5116C24.2575 14.1772 23.9116 13.7278 23.2198 12.8291C21.8385 11.0345 19.5218 9 16.118 9C12.7142 9 10.3975 11.0345 9.01621 12.8291C8.32441 13.7278 7.97852 14.1772 7.97852 15.5116ZM16.118 12.4593C14.4323 12.4593 13.0657 13.8258 13.0657 15.5116C13.0657 17.1973 14.4323 18.5639 16.118 18.5639C17.8037 18.5639 19.1703 17.1973 19.1703 15.5116C19.1703 13.8258 17.8037 12.4593 16.118 12.4593Z"
              fill="#FFF3F0"
              fill-rule="evenodd"
            />
          </svg>
        </span>
        <p :class="$style.titleText">{{ title }}</p>
      </div>

      <NuxtLink
        v-if="seeMoreUrl"
        :class="$style.seeMore"
        :draggable="false"
        no-prefetch
        :to="seeMoreUrl"
        @click="emit('seeMore')"
      >
        VER MAIS
      </NuxtLink>
    </div>

    <PerfilOficialSwiper
      ref="swiperRef"
      :card-height="cardHeight"
      :gap="spaceBetweenInPixels"
    >
      <template #default="{ handleClick }">
        <div
          v-for="(game, index) in mappedGames"
          :key="game.slug || index"
          :class="$style.swiperItem"
        >
          <CasinoMediaApostatudo
            :class="$style.mediaCard"
            :game="game"
            :index="index"
            :loading="getLoadingStrategy(index, 0)"
            type="game"
            @click="handleClick"
          />
        </div>
      </template>
    </PerfilOficialSwiper>
  </div>
</template>

<style module lang="postcss">
.lastPlayed {
  @apply flex flex-col select-none w-full mb-2 md:mb-3;
}

.header {
  @apply flex justify-between items-center rounded-md mb-2.5 lg:mb-4 pr-[10px];
  @apply [background:linear-gradient(270deg,rgba(255,255,255,0.05)_0%,rgba(255,255,255,0.00)_100%)];
  user-select: none;
}

.titleContainer {
  @apply flex items-center h-[31px] lg:h-[45px] gap-2.5 md:gap-2;
}

.headerIcon {
  @apply flex-shrink-0 flex items-center justify-center;
  width: 32px;
  height: 31px;
}

.headerIcon svg {
  width: 100%;
  height: 100%;
  display: block;
}

.titleText {
  @apply text-white text-[16px] lg:text-[20px] font-bold leading-4;
}

.seeMore {
  @apply flex items-center justify-center ml-auto gap-1 px-2.5 text-white text-[11px] font-bold leading-4;
  border-radius: 5.11px;
  border: 1.022px solid rgba(255, 255, 255, 0.1);
  padding: 2.044px 7.154px;
}

.swiperItem {
  min-width: v-bind(mediaItemWidth);
  width: v-bind(mediaItemWidth);
  flex-shrink: 0;
  /* Sem aspect-ratio aqui: o card precisa caber imagem (1:1.16, reservada
     pelo pt-[116%] do .imageWrapper) + badge "Pagou hoje" abaixo. Travar
     o slide em 1:1.16 cortaria o badge. */
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
}

.mediaCard {
  @apply relative;
  z-index: 1;
  width: 100%;
  max-width: none;
}
</style>
